import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { pets as petsApi } from '../utils/api';
import axios from 'axios';
import useSubjectSettings from '../hooks/useSubjectSettings';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AddPetModal = ({ client, onClose, onSuccess }) => {
  const { singularName: subjectSingularName } = useSubjectSettings();
  const [subjectFields, setSubjectFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubjectFields();
  }, []);

  const loadSubjectFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/subject-fields/active`);
      setSubjectFields(response.data);
      
      // Initialize form data with empty values
      const initialData = {};
      response.data.forEach(field => {
        initialData[field.field_name] = '';
      });
      setFormData(initialData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load subject fields:', error);
      toast.error('Failed to load form fields');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = subjectFields.filter(f => f.is_required);
    for (const field of requiredFields) {
      if (!formData[field.field_name] || formData[field.field_name].toString().trim() === '') {
        toast.error(`${field.field_label} is required`);
        return;
      }
    }
    
    setSubmitting(true);

    try {
      await petsApi.create({
        ...formData,
        ownerId: client.id,
      });

      toast.success(`${formData.name || subjectSingularName} added successfully!`);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Add pet error:', error);
      toast.error(error.response?.data?.error || `Failed to add ${subjectSingularName.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Add {subjectSingularName}</h2>
            <p className="text-sm text-gray-600 mt-1">
              For {client.first_name} {client.last_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <p className="text-gray-600">Loading form...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {subjectFields.map((field) => {
              if (field.field_type === 'select') {
                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.field_label} {field.is_required && '*'}
                    </label>
                    <select
                      name={field.field_name}
                      value={formData[field.field_name] || ''}
                      onChange={handleChange}
                      required={field.is_required}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select {field.field_label}</option>
                      {field.options?.map((opt, idx) => (
                        <option key={idx} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              if (field.field_type === 'checkbox') {
                const cleanOptions = (field.options || []).filter(o => typeof o === 'string' && o.trim() !== '');
                if (cleanOptions.length === 0) {
                  return (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.field_label} {field.is_required && '*'}
                      </label>
                      <input
                        type="text"
                        name={field.field_name}
                        value={formData[field.field_name] || ''}
                        onChange={handleChange}
                        placeholder={field.placeholder || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  );
                }
                const selected = Array.isArray(formData[field.field_name]) ? formData[field.field_name] : [];
                const isOther = (opt) => typeof opt === 'string' && opt.toLowerCase() === 'other';
                const otherChecked = selected.some((v) => typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:')));
                const otherText = (selected.find((v) => typeof v === 'string' && v.toLowerCase().startsWith('other:')) || '').replace(/^other:\s*/i, '');

                const setSelected = (next) => setFormData(prev => ({ ...prev, [field.field_name]: next }));

                const toggleOption = (option, checked) => {
                  const next = checked ? [...selected, option] : selected.filter(v => v !== option);
                  setSelected(next);
                };

                const toggleOther = (checked) => {
                  const cleaned = selected.filter((v) => !(typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:'))));
                  setSelected(checked ? [...cleaned, 'Other'] : cleaned);
                };

                const updateOtherText = (text) => {
                  const cleaned = selected.filter((v) => !(typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:'))));
                  setSelected([...cleaned, text.trim() ? `Other: ${text}` : 'Other']);
                };

                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.field_label} {field.is_required && '*'}
                    </label>
                    <div className="space-y-2">
                      {cleanOptions.filter(o => !isOther(o)).map((option, idx) => (
                        <label key={idx} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selected.includes(option)}
                            onChange={(e) => toggleOption(option, e.target.checked)}
                            className="rounded"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={otherChecked}
                            onChange={(e) => toggleOther(e.target.checked)}
                            className="rounded"
                          />
                          <span className="text-sm">Other</span>
                        </label>
                        {otherChecked && (
                          <input
                            type="text"
                            value={otherText}
                            onChange={(e) => updateOtherText(e.target.value)}
                            placeholder="Please specify..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mt-2 ml-6"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              if (field.field_type === 'textarea') {
                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.field_label} {field.is_required && '*'}
                    </label>
                    <textarea
                      name={field.field_name}
                      value={formData[field.field_name] || ''}
                      onChange={handleChange}
                      required={field.is_required}
                      placeholder={field.placeholder || ''}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                  </div>
                );
              }

              // Default: text/number/email/tel inputs
              return (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.field_label} {field.is_required && '*'}
                  </label>
                  <input
                    type={field.field_type}
                    name={field.field_name}
                    value={formData[field.field_name] || ''}
                    onChange={handleChange}
                    required={field.is_required}
                    placeholder={field.placeholder || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              );
            })}

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Adding...' : `Add ${subjectSingularName}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddPetModal;
